import { yupResolver } from "@hookform/resolvers/yup";
import { message } from "antd";
import dayjs from "dayjs";
import {
  CircuitList,
  CircuitListData,
} from "components/inputs/circuits/CircuitList";
import { OptionBar } from "components/inputs/OptionBar";
import { Radio } from "components/inputs/Radio";
import { SolutionRadio } from "components/inputs/SolutionRadio";
import { ProgressBar, ProgressBarItem } from "components/ProgressBar";
import Button from "components/shared/button";
import Input, { InputType } from "components/shared/inputs/input";
import Loader from "components/shared/loader";
import { SvgType_1, SvgType_2, SvgType_3, SvgType_4 } from "components/svg";
import { usePopup } from "providers/PopupProvider";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useFormState, useWatch } from "react-hook-form";
import logoUnderOptionSup from "styles/images/logoUnderOptionsSupp.png";

import Select from "components/shared/inputs/select";
import {
  useGetDepartmentsQuery,
  useGetVillesQuery,
  useLazyGetLocationInfoQuery,
  useUpdateProjectTraceMutation,
} from "store/api";
import { setSiteName } from "store/commonSlice";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { ProjectTrace } from "types/ProjectTrace";
import { filterObject, formatMinutes } from "utils";
import { AppCode, SubAppCode } from "utils/engine/consts";
import {
  getZon4Defaults,
  getZone1Data,
  getZone4FieldNames,
  useEngine,
} from "utils/engine/engine";
import { generateUniqueId } from "utils/helpers";
import {
  getLocalStorageTraceId,
  setLocalStorageTraceId,
} from "utils/helpers/localStorage";
import MapValuesToProjectTrace from "utils/helpers/projectTrace";
import { useAuth } from "utils/hooks/useAuth";
import useCustomForm from "utils/hooks/useCustomForm";
import { useDebouncedFunction } from "utils/hooks/useDebouncedFunc";
import { useSalesNetwork } from "utils/hooks/useSalesNetworks";
import useTranslation from "utils/hooks/useTranslation";
import { EtudeStep } from "utils/defines";
import * as yup from "yup";
import { SavePopup } from "../popups/SavePopup";
import {
  applicationImageMap,
  buildZone1ValidationSchema,
  calcFields,
  calcFieldsSchema,
  calcFieldTypes,
  calculatedDataOrder,
  EcsProdFields,
  etape2Schema,
  EtudeFormFields,
  EtudeTypes,
  LocInstFields,
  restrictionsFields,
  SubAppCodeType,
  useSubApplicationOptions,
} from "./constants";
import { useCurrentEtudeData } from "./hooks/useCurrentEtudeData";
import { useEtudeUpdate } from "./hooks/useEtudeUpdate";
import { EcsProductionItem } from "./ProgressBarItems/EcsProductionItem";
import { ExpertModeFields } from "./ProgressBarItems/ExpertModeFields";
import { InstallationRestrictions } from "./ProgressBarItems/InstallationRestrictions";
import { SimulationOptions } from "./ProgressBarItems/SimulationOptions";
import { useGetSolutions } from "./Solutions";
import { useGetSubAppFields } from "./Utils/getSubAppFields";
import { RadioGroupDelay } from "components/RadioGroupDelay";
import Checkbox from "components/shared/inputs/checkbox";
export type EtudeType = (typeof EtudeTypes)[keyof typeof EtudeTypes];
let defaultValues = {
  projectType: null,
  installationType: null,
  antiCorrosion: "no",
  boilerForEcs: "yes",
  circuits: [
    {
      id: generateUniqueId(),
      name: "Circuit 1",
      underfloorHeating: "no",
      type: undefined,
    },
  ],
  antiLegionellaSupport: "no",
  expertModeType: "appartement",
  siteName: "",
  ecs_par_repas: 4,
  simulationOption: true,
  // altTbaseChanged: false,
  inputDelay: null,
};

const hybridOnlyFieldDefaults: Partial<EtudeFormFields> = {
  continueStudyHybrid: false,
  TcoupureQuestion: false,
  Tcoupure: null,
  boilerForEcs: null,
};

export const EtudeForm: React.FC = () => {
  const { isAuth } = useAuth();
  const { t } = useTranslation();
  const { network } = useSalesNetwork();
  const { openPopup, closeLastPopup } = usePopup();
  const dispatch = useAppDispatch();
  const [calculatedSolutions, setCalculatedSolutions] =
    useState<EtudeSolutionsResponseData | null>(null);
  const [validationSchema, setValidationSchema] =
    useState<yup.ObjectShape | null>({
      siteName: yup.string().required(t("ERR_SITENAME_REQUIRED")),
      city: yup.string().required(t("ERR_CITY_REQUIRED")),
      fin_nc: yup.string().required(t("ERR_END_PERIOD_REQUIRED")),
      debut_nc: yup.string().required(t("ERR_START_PERIOD_REQUIRED")),
      inputDelay: yup.string().required(""),
    });
  const [besoinCalculatedData, setBesoinCalculatedData] = useState<
    | {
      V10: number;
      tph: number;
      Vj: number;
      Vph: number;
    }
    | { detail: any }
    | null
  >(null);
  const [loadingCalculatedData, setLoadingCalculatedData] = useState(false);
  const [simulationOptionsData, setSimulationOptionsData] = useState(null);
  const [instLocCircuitsData, setInstLocCircuitsData] = useState<any>({});
  const [resetExpertMode, setResetExpertMode] = useState(false);
  const [autoRecalc, setAutoRecalc] = useState(false);
  const [calculationTriggered, setCalculationTriggered] = useState(false);
  const [callCalculate2, setCallCalculate2] = useState(false);
  // const [isContinueStudyChecked, setIsContinueStudyChecked] = useState(false);

  const { json, service, hasEcs, hasChauffage, etudeData } =
    useCurrentEtudeData();

  const form = useCustomForm<EtudeFormFields>({
    preserveData: true,
    defaultValues: defaultValues as any,
    resolver: yupResolver(yup.object(validationSchema ?? {})) as any,
    mode: "onChange",
    context: { service },
  });

  const {
    control,
    register,
    setValue,
    watch,
    handleSubmit,
    reset,
    getValues,
    getFieldState,
    clearErrors,
    resetField,
    formState: { isDirty, errors },
  } = form;
  const { noSolution } = useAppSelector((state) => state.etude);
  const { dirtyFields } = useFormState({
    control,
    name: ["city", "installationType"],
  });

  const cityIsDirty = !!dirtyFields.city;
  const installationTypeIsDirty = !!dirtyFields.installationType;
  const startPeriod = watch("debut_nc");

  const endPeriodMinDate = useMemo(() => {
    if (!startPeriod) return null;
    const [day, month] = String(startPeriod).split("/").map(Number);
    if (!day || !month) return null;
    return dayjs().month(month - 1).date(day).startOf("day");
  }, [startPeriod]);

  // Watch all T fields
  const tValues = watch(["nbT1", "nbT2", "nbT3", "nbT4", "nbT5", "nbT6"]) || [];

  // Compute total only after first calculation
  const totalSum = tValues.reduce(
    (acc: number, val) => acc + (Number(val) || 0),
    0
  );

  const handleCalculate = async () => {
    const isValid = await form.trigger("Pboucle");
    if (!isValid) return;
    setCalculationTriggered(true);
    calculate2();
  };

  useEffect(() => {
    if (json) {
      reset(json);
    }
  }, [json, reset]);

  const [
    departement,
    location,
    installationType,
    application,
    subApplication,
    altitude,
    montagne,
    pboucle,
    systeme,
    circuits,
    NbMPJ,
    expertModeType,
    etudeFormSolutionId,
    siteName,
    tecs,
    tp,
    tefs,
    inputDelay
  ] = watch([
    "departement",
    "city",
    "installationType",
    "application",
    "subApplication",
    "altitude",
    "bdm_montagne_defaut",
    "Pboucle",
    "systeme",
    "circuits",
    "NbMPJ",
    "expertModeType",
    "etudeFormSolutionId",
    "siteName",
    "TECS",
    "Tp",
    "TEFS",
    "inputDelay"
  ]);

  const { isSolutionsFetching, onSearchSolutions } = useGetSolutions(
    handleSubmit,
    hasEcs,
    hasChauffage,
    calculatedSolutions?.solutions &&
      etudeFormSolutionId !== null &&
      etudeFormSolutionId
      ? calculatedSolutions?.solutions[+etudeFormSolutionId]
      : null,
    service
  );
  const formData = useWatch({ control });

  const { handleUpdateJson, isLoadingEtudeJsonSave } = useEtudeUpdate(
    siteName,
    {
      ...formData,
      step: EtudeStep.InputForm,
      stepName: t("FO_STUDIES_IN_PROGRESS"),
      service: service,
    }
  );

  const [updateProjectTrace] = useUpdateProjectTraceMutation();

  const types = circuits?.map((circuit: any) => circuit.type);
  const calculationFormRef = useRef<HTMLFormElement>(null);
  const prevFieldValues = useRef<{
    location: string | null;
    application: string | null;
    subApplication: (typeof SubAppCode)[keyof typeof SubAppCode] | null;
    circuits: CircuitListData | null;
  }>({
    location: null,
    application: null,
    subApplication: null,
    circuits: null,
  });
  const dataForCalcul2 = useRef<any>(null);

  const {
    defaultData,
    calculatedData,
    allData,
    sendRequest,
    setCalculatedData,
    expertModeChanged,
  } = useEngine({
    application: application,
    subapplication: subApplication,
    data: getValues(),
    resetExpertMode: resetExpertMode,
  } as any);

  const calculate = useCallback(() => {
    if (Object.keys(errors).length === 0) {
      setLoadingCalculatedData(true);

      calcFieldsSchema.batiment.type =
        calcFieldTypes[subApplication as keyof typeof calcFieldTypes];
      sendRequest(
        filterObject(getValues(), calcFieldsSchema as any, expertModeType),
        `moteur/ecs/besoin`
      )
        .then((res: any) => {
          if (res.detail) {
            message.error(t(`ERR_MOTEUR_${res.detail[0]?.code}`));
            setBesoinCalculatedData(null);
            setAutoRecalc(false);
          } else {
            setBesoinCalculatedData(res);
            console.log("setcallCalculate2", callCalculate2);
            setCallCalculate2(true);
            for (const key in res) {
              setValue(key as keyof EtudeFormFields, res[key]);
            }
            prevValues.current = getValues();
          }
        })
        .catch((err: any) => {
          // Handle 500 server error
          if (err?.response?.status === 500) {
            message.error(t("ERR_MOTEUR_500")); // translate key or fallback text
          }
          setBesoinCalculatedData(null);
        })
        .finally(() => {
          setLoadingCalculatedData(false);
        });
    } else {
      setBesoinCalculatedData(null);
      setAutoRecalc(false);
    }
  }, [
    subApplication,
    calcFieldsSchema,
    getValues,
    expertModeType,
    sendRequest,
    setLoadingCalculatedData,
    setBesoinCalculatedData,
  ]);

  useEffect(() => {
    (window as any).IsInFormPage = true;
    return () => {
      (window as any).IsInFormPage = false;
    };
  }, []);

  const prevValues = useRef<any>(getValues());

  useEffect(() => {
    prevValues.current = getValues();
  }, []);

  const handleBlur = useCallback(
    (field: keyof typeof formData) => {
      return (_e: React.FocusEvent) => {
        if (!autoRecalc) return;
        const fieldState = getFieldState(field);
        if (!fieldState?.isDirty) return; // only calculate if the user actually changed the field
        calculate();
      };
    },
    [autoRecalc, calculate, getFieldState]
  );

  useEffect(() => {
    for (const key in calculatedData) {
      if (
        calculatedData[key] !== undefined &&
        calculatedData[key] != formData[key as keyof EtudeFormFields]
      ) {
        if (
          isDirty ||
          resetExpertMode
          //  && isExpertModeField(key, subApplication)
        ) {
          setValue(key as keyof EtudeFormFields, calculatedData[key]);
        }
      }
    }
    setResetExpertMode(false);
  }, [calculatedData, setValue]);

  useEffect(() => {
    if (getFieldState("subApplication").isDirty && defaultData) {
      setCalculatedData(null);
      setValue("expertMode", false);
      setBesoinCalculatedData(null);
      [...calcFields, ...EcsProdFields].forEach((field) => {
        setValue(field as any, defaultData[field]?.default);
      });
      calcFields.forEach((field) => {
        clearErrors(field as keyof EtudeFormFields);
      });
      setCalculatedSolutions(null);
    }
  }, [defaultData]);

  useEffect(() => {
    if (isDirty) {
      setBesoinCalculatedData(null);

      setCalculationTriggered(false);
      setAutoRecalc(false);
    }
    if (subApplication && !isDirty) {
      calculate();
    }
  }, [subApplication]);

  const { data: departments } = useGetDepartmentsQuery();

  const { data: villes } = useGetVillesQuery(
    { depCode: departement || "" },
    { skip: !departement }
  );

  const ville = villes?.find((v) => v.value === location);

  const calculationFormIsOpen = !!application;

  const [
    getLocationInfo,
    {
      data: locationInfo,
      isLoading: isLocationInfoLoading,
      isFetching: isLocationInfoFetching,
    },
  ] = useLazyGetLocationInfoQuery();

  const handleTrace = useCallback(async () => {
    let id = getLocalStorageTraceId();

    let projectTrace: ProjectTrace = MapValuesToProjectTrace(
      {
        Id: id || 0,
        network: network?.code,
        service: service,
        ...getValues(),
        etudeData,
        noSolution: noSolution,
      },
      t
    );

    let project = await updateProjectTrace(projectTrace);

    if (project?.data?.id) {
      setLocalStorageTraceId(project.data.id);
    }
  }, [network, service, etudeData]);

  const fields = useGetSubAppFields({
    subApplication,
    control,
    allData,
    t,
    getFieldState,
    watch,
    NbMPJ,
    totalSum,
    autoRecalc,
    recalc: calculate,
    getValues,
    setLoadingCalculatedData
  });

  const schemaWithUpdatedType = useMemo(() => {
    return {
      ...etape2Schema,
      batiment: {
        ...etape2Schema.batiment,
        type: calcFieldTypes[subApplication as keyof typeof calcFieldTypes],
      },
    };
  }, [etape2Schema, subApplication]);

  const etape2SchemaWithOptions = useMemo(() => {
    if (systeme && network?.code) {
      const optionsFieldNames = getZone4FieldNames(systeme, network?.code);
      return optionsFieldNames.reduce(
        (acc, fieldName) => ({ ...acc, [fieldName]: null }),
        { ...schemaWithUpdatedType, ...restrictionsFields }
      );
    }
  }, [
    getZone4FieldNames,
    systeme,
    network?.code,
    schemaWithUpdatedType,
    restrictionsFields,
  ]);

  const calculate2 = useCallback(() => {
    if (
      !(
        pboucle !== undefined &&
        pboucle !== "" &&
        pboucle !== null &&
        tecs &&
        tp &&
        tefs &&
        !getFieldState("Pboucle").invalid
      )
    ) {
      setCalculatedSolutions(null);
      return;
    }
    if (isDirty)
      setValue("etudeFormSolutionId", null, {
        shouldDirty: true,
        shouldValidate: true,
      });
    const values = getValues();

    const data = {
      ...filterObject(values, etape2SchemaWithOptions as any, expertModeType),
      mode: "SI/SA",
      reseau_commercial: network?.codeReseauCommercial,
    };

    // const dataForCalculation = {
    //   systeme: values.systeme,
    //   pboucle: values.Pboucle,
    //   tecs: values.TECS,
    //   tp: values.Tp,
    //   tefs: values.TEFS,
    //   antiLegionellaSupport: values.antiLegionellaSupport,
    //   antiLegionella: values.antiLegionella,
    //   largeur_porte: values.largeur_porte,
    //   hauteur_porte: values.hauteur_porte,
    //   nbBallon: values.nbBallon,
    //   Jaquette: values.Jaquette,
    //   Ouverture: values.Ouverture,
    //   circPrim: values.circPrim,
    //   circSec: values.circSec,
    //   brand: values.brand,
    //   circ: values.circ,
    // };

    if (
      dataForCalcul2.current &&
      JSON.stringify(dataForCalcul2.current) === JSON.stringify(data)
    ) {
      return; // skip calculation
    }

    dataForCalcul2.current = data;

    setLoadingCalculatedData(true);
    sendRequest(data, `moteur/ecs/solution`)
      .then((res: any) => {
        if (res.detail) message.error(t(`ERR_MOTEUR_${res.detail[0]?.code}`));
        setCalculatedSolutions(res);
        setValue("calculatedSolutions", res?.solutions, {
          shouldDirty: true,
          shouldValidate: true,
        });
      })
      .catch((err: any) => {
        if (err.response?.status === 500) {
          message.error(t("ERR_MOTEUR_500"));
        }
        setCalculatedSolutions(null);
      })
      .finally(() => {
        handleTrace();
        setLoadingCalculatedData(false);
      });
  }, [
    pboucle,
    tecs,
    tp,
    tefs,
    subApplication,
    systeme,
    network?.codeReseauCommercial,
    getValues,
    etape2SchemaWithOptions,
    sendRequest,
    handleTrace,
    setLoadingCalculatedData,
    setCalculatedSolutions,
  ]);

  useEffect(() => {
    if (callCalculate2 && calculationTriggered) {
      calculate2();
    }
    setCallCalculate2(false);
  }, [callCalculate2]);

  const subApplicationOptions = useSubApplicationOptions(
    t,
    network,
    service,
    application
  );

  const getLocationInfoDebounced = useDebouncedFunction(getLocationInfo, 1000);

  const onClickRestoreExpertMode = () => {
    setValue("resetExpertMode", true);
    setResetExpertMode(true);
    const calcTimeout = setTimeout(() => {
      if (besoinCalculatedData) {
        calculate();
      }
    }, 2000);

    return () => clearTimeout(calcTimeout);
  };

  useEffect(() => {
    if (!allData) return;
    let schema: yup.ObjectShape = {};

    Object.entries(allData).forEach(([key, value]) => {
      const v = value as any;
      const errorMessage = t(v.errorMessage, [], "message");

      if (v.minimum !== undefined || v.maximum !== undefined) {
        let fieldSchema = yup
          .number()
          .transform((value, originalValue) =>
            originalValue === "" ? null : value
          )
          .nullable()
          .notRequired();

        if (v.minimum !== undefined) {
          fieldSchema = fieldSchema.min(v.minimum, errorMessage);
        }

        if (v.maximum !== undefined) {
          fieldSchema = fieldSchema.max(v.maximum, errorMessage);
        }

        schema[key] = fieldSchema;
      }
    });

    setValidationSchema((prev) => ({ ...prev, ...schema }));
  }, [allData, t]);

  useEffect(() => {
    if (!defaultData) {
      return;
    }
    const entries = Object.entries(defaultData);
    const defaults: any = {};
    for (const [key, value] of entries) {
      const v = value as any;
      defaults[key] =
        calcFields.indexOf(key as any) !== -1 && v.default === ""
          ? undefined
          : v.default;
    }
    if (isDirty) {
      for (const key in defaults) {
        if (defaults[key] !== undefined) {
          setValue(key as keyof EtudeFormFields, defaults[key]);
        }
      }
    }
  }, [getValues, defaultData]);

  useEffect(() => {
    if (!location) {
      return;
    }
    getLocationInfo({ departement: departement!, code: location! });
  }, [getLocationInfo, location]);

  useEffect(() => {
    let zone1Data = getZone1Data(installationType, location, circuits);
    let schema = buildZone1ValidationSchema({
      circuits,
      requireByService: true,
      t,
      zone1Data,
    });
    setInstLocCircuitsData(zone1Data?.allData);

    const defaultEntries = Object.entries(zone1Data.defaults);
    const defaults: any = {};

    for (const [key, value] of defaultEntries) {
      const v = value as any;
      if (v !== undefined && v !== "empty") {
        defaults[key] = v;
      } else {
        defaults[key] = undefined;
      }
    }

    if (isDirty) {
      for (const key in defaults) {
        if (defaults[key] !== undefined) {
          if (
            LocInstFields.find((fieldName) => fieldName == key) &&
            prevFieldValues.current?.location === location
          ) {
            continue;
          }
          setValue(key as keyof EtudeFormFields, defaults[key]);
        }
      }
      clearErrors(["altitude", "t_base", "t_ete"]);

      prevFieldValues.current.location = location;
    }

    setValidationSchema((prev) => ({ ...prev, ...schema }));
  }, [location, installationType, JSON.stringify(types)]);

  useEffect(() => {
    if (!location) return;
    const altitudeDirty = getFieldState("altitude").isDirty;
    const montagneDirty = getFieldState("bdm_montagne_defaut").isDirty;
    if (!altitudeDirty && !montagneDirty) return;

    if (!getFieldState("altitude").invalid && !locationInfo?.detail) {
      getLocationInfoDebounced({
        departement: departement!,
        code: location!,
        altitude: altitude ?? undefined,
        bord_de_mer_montagne: montagne !== null ? montagne === "yes" : undefined,
      });
    }
  }, [altitude, montagne]);

  useEffect(() => {
    if (!ville) {
      return;
    }

    if (cityIsDirty) {
      if (ville.bdm_montagne_defaut === 1 || ville.bdm_montagne_defaut === 3) {
        setValue("bdm_montagne_defaut", "yes", { shouldDirty: false });
      } else if (
        ville.bdm_montagne_defaut === 2 ||
        ville.bdm_montagne_defaut === 4
      ) {
        setValue("bdm_montagne_defaut", "no", { shouldDirty: false });
      } else {
        setValue("bdm_montagne_defaut", null, { shouldDirty: false });
      }
    }
  }, [setValue, ville]);

  useEffect(() => {
    if (!locationInfo || isLocationInfoLoading) {
      return;
    }
    setValue("LocationInfo", locationInfo, { shouldDirty: false });
    if (getFieldState("altitude").isDirty || getFieldState("city").isDirty) {
      setValue("altitude", locationInfo.altitude, { shouldDirty: false });
      setValue("t_base", locationInfo.t_base, { shouldDirty: false });
      if (locationInfo.periode_ete) {
        const parts = locationInfo.periode_ete.split("-");
        if (parts.length === 2) {
          setValue("debut_nc", parts[1], { shouldDirty: false });
          setValue("fin_nc", parts[0], { shouldDirty: false });
        }
      }
    }
  }, [isLocationInfoLoading, locationInfo, setValue]);

  useEffect(() => {
    if (!getFieldState("application").isDirty) return;

    // Reset subApplication
    if (subApplicationOptions?.length === 1) {
      setValue(
        "subApplication",
        subApplicationOptions[0].value as SubAppCodeType
      );
    } else {
      setValue("subApplication", null);
    }

    // Reset ECS production fields
    EcsProdFields.forEach((field) =>
      setValue(field as keyof EtudeFormFields, null)
    );
    calcFields.forEach((field) =>
      setValue(field as keyof EtudeFormFields, null)
    );

    // Reset calculated states
    setCalculatedSolutions(null);
    setCalculatedData(null);
    setBesoinCalculatedData(null);
    setAutoRecalc(false);
    dataForCalcul2.current = null;

    prevValues.current = {};

    // Reset expert mode
    setValue("expertMode", false);
  }, [application, subApplicationOptions, setValue]);

  //TODO: Maria check and uncomment if needed, remove if not needed https://dev.azure.com/ogmadev/Thercodim/_workitems/edit/971
  //the requirement says that the default data should not be reset on calculate 1
  //should reset on subApplication change which is done in the useEffect above [getvalues,defaultData]
  // useEffect(() => {
  //   if (!defaultData) {
  //     return;
  //   }
  //   const defaults: any = {};

  //   EcsProdFields.forEach(
  //     (field) =>
  //       (defaults[field] = defaultData[field]
  //         ? defaultData[field].default
  //         : defaultValues[field as keyof typeof defaultValues])
  //   );

  //   if (isDirty) {
  //     for (const key in defaults) {
  //       if (defaults[key] !== undefined) {
  //         setValue(key as keyof EtudeFormFields, defaults[key]);
  //       }
  //     }
  //   }
  // }, [setValue, besoinCalculatedData]);

  useEffect(() => {
    if (network && systeme) {
      let zone4Data = getZon4Defaults(systeme, network.code, {
        Pboucle: !!pboucle,
      });

      setSimulationOptionsData(zone4Data?.allData);

      if (isDirty && zone4Data?.defaults) {
        for (const key in zone4Data?.defaults) {
          if (zone4Data?.defaults[key] !== undefined) {
            setValue(key as keyof EtudeFormFields, zone4Data?.defaults[key]);
          }
        }
      }
    }
  }, [systeme, getZon4Defaults, network, pboucle, setValue]);

  useEffect(() => {
    if (
      calculatedSolutions?.solutions &&
      etudeFormSolutionId &&
      etudeFormSolutionId !== null
    )
      setValue(
        "etudeFormSolution",
        calculatedSolutions?.solutions[+etudeFormSolutionId]
      );
    if (!!etudeFormSolutionId) {
      setCalculationTriggered(true);
    }
  }, [etudeFormSolutionId]);

  useEffect(() => {
    if (!installationTypeIsDirty) {
      return;
    }

    Object.entries(hybridOnlyFieldDefaults).forEach(([field, value]) => {
      setValue(field as keyof EtudeFormFields, value as never, {
        shouldDirty: false,
        shouldValidate: false,
      });
    });
    clearErrors(Object.keys(hybridOnlyFieldDefaults) as (keyof EtudeFormFields)[]);

    if (installationType === "2") {
      setValue("boilerForEcs", "yes", {
        shouldDirty: false,
        shouldValidate: false,
      });
    }
  }, [clearErrors, installationType, setValue, installationTypeIsDirty]);

  useEffect(() => {
    if (etudeData && etudeData.siteName) {
      setValue("siteName", etudeData?.siteName || "");
    }
  }, [etudeData?.siteName]);

  const handleNextStepClick = useCallback(async () => {
    await handleTrace();

    onSearchSolutions();
  }, [onSearchSolutions, handleTrace]);

  const onInvalid = () => {
    message.error(t("FO_SIMULATION_INFO_MISSING"));
  };

  const resetLocationFields = useCallback(() => {
    setValue("altTbaseChanged", false, { shouldDirty: false, shouldValidate: false });

    resetField("altitude", { defaultValue: null });
    resetField("t_base", { defaultValue: null });
    resetField("t_ete", { defaultValue: null });
    resetField("bdm_montagne_defaut", { defaultValue: null });
  }, [setValue, resetField]);

  const onChangeAltTbase = (e: any, field: "altitude" | "t_base") => {
    setValue(field, e, {
      shouldDirty: true,
      shouldValidate: true,
    });
    setValue("altTbaseChanged", true);
  };
  const isEcsLastStepOpen =
    besoinCalculatedData !== null && systeme !== null && !!calculatedSolutions;

  const isEcsSaveOpen =
    besoinCalculatedData !== null &&
    systeme !== null &&
    !!calculatedSolutions &&
    etudeFormSolutionId !== null;
  const isHybridBlocked = installationType === "2" && !watch("continueStudyHybrid");

  const isBesoinCalculatedDataOpen = installationType !== null && installationType === "2" ? !isHybridBlocked
    : true;

  const onSubmit = () => {
    dispatch(setSiteName(siteName));
    handleNextStepClick();
  };

  useEffect(() => {
    if (besoinCalculatedData) {
      setAutoRecalc(true);
      !!etudeFormSolutionId && setCalculationTriggered(true);
    }
  }, [besoinCalculatedData]);

  // Sync etudeFormSolutionId from Redux back to form when popup updates it
  useEffect(() => {
    if (json?.etudeFormSolutionId !== undefined && json?.etudeFormSolutionId !== null) {
      setValue("etudeFormSolutionId", json.etudeFormSolutionId.toString(), {
        shouldDirty: false,
      });
    }
  }, [json?.etudeFormSolutionId, setValue]);

  return (
    <group data-align="center" data-direction="column" data-min-height="fit">
      <group data-space-vertical="30" data-max-length="1440">
        {(isSolutionsFetching ||
          isLoadingEtudeJsonSave ||
          loadingCalculatedData) && <Loader />}
        <FormProvider {...(form as any)}>
          <form>
            <ProgressBar>
              {network?.displayInpNexRenovation && (
                <ProgressBarItem
                  isOpen={true}
                  title={t("FO_SIMULATION_PROJECT_TYPE")}
                >
                  <Radio
                    control={control}
                    name="projectType"
                    options={[
                      {
                        value: "1",
                        text: t("FO_SIMULATION_RENOVATION"),
                        icon: <SvgType_1 />,
                      },
                      {
                        value: "2",
                        text: t("FO_SIMULATION_NEW_BUILT"),
                        icon: <SvgType_2 />,
                      },
                    ]}
                  />
                </ProgressBarItem>
              )}
              <ProgressBarItem
                isOpen={
                  watch("projectType") !== null ||
                  !network?.displayInpNexRenovation
                }
                title={t("FO_SIMULATION_INSTALLATION_TYPE")}
              >
                <group data-direction="column" data-gap="20">
                  <Radio
                    control={control}
                    name="installationType"
                    options={[
                      ...(network?.displayInpElec
                        ? [
                          {
                            value: "1",
                            text: t("FO_SIMULATION_DES_PAC"),
                            desc: t("FO_SIMULATION_TEXT_PAC"),
                            icon: <SvgType_3 />,
                          },
                        ]
                        : []),
                      ...(network?.displayInpHybrid && (!hasEcs || hasChauffage)
                        ? [
                          {
                            value: "2",
                            text: t("FO_SIMULATION_DES_HYBRID"),
                            desc: t("FO_SIMULATION_TEXT_HYBRID"),
                            // desc: t(
                            //   hasEcs
                            //     ? "FRONT_HYBRIDE_DS_INDICATION"
                            //     : "FO_SIMULATION_TEXT_HYBRID"
                            // ),
                            // disabled: hasEcs,
                            // descColor: hasEcs ? "main" : undefined,
                            icon: <SvgType_4 />,
                          },
                        ]
                        : []),
                    ]}
                  />
                  {installationType !== null && installationType === "2" &&
                    <group data-width="auto">
                      <Checkbox
                        name="continueStudyHybrid"
                        control={control}
                        type="checkbox"
                        label={t("FO_INFO_HYBRIDE_PAC_GAZ_Message_1")}
                      />
                    </group>
                  }
                </group>
              </ProgressBarItem>
              <ProgressBarItem
                isOpen={installationType !== null && !isHybridBlocked}
                title={t("FO_SIMULATION_LOCATION")}
              >
                <group data-direction="column" data-gap="10">
                  <group data-gap="20">
                    <Select
                      dataLength="400"
                      label="FO_SIMULATION_DEPARTMENT"
                      name="departement"
                      control={control}
                      allowSearch
                      searchOptions={{
                        caseSensitive: false,
                        noAccent: true,
                        contains: true,
                      }}
                      helpText={t("FO_HELP_DEPARTMENT")}
                      options={departments}
                      onChange={(e: any) => {
                        setValue("departement", e, { shouldDirty: true });
                        setValue("dep_code", departments?.find(d => d.value === e)?.code, { shouldDirty: true });
                        setValue("city", null, { shouldDirty: false, shouldValidate: false });
                        setValue("villeData", null, { shouldDirty: false, shouldValidate: false });

                        resetLocationFields();
                      }}
                    />
                    <Select
                      dataLength="400"
                      label="FO_SIMULATION_CITY"
                      name="city"
                      control={control}
                      allowSearch
                      searchOptions={{
                        caseSensitive: false,
                        noAccent: true,
                        contains: true,
                      }}
                      helpText={t("FO_HELP_CITY")}
                      options={villes}
                      onChange={(e: any) => {
                        setValue("city", e, { shouldDirty: true });
                        setValue("villeData", ville);
                        resetLocationFields();
                      }}
                    />
                  </group>
                  <group data-gap="20">
                    <Input
                      dataLength="400"
                      control={control}
                      type={InputType.Number}
                      disabled={!location || isLocationInfoFetching}
                      name="altitude"
                      label="FO_SIMULATION_ALTITUDE"
                      helpText={t("FO_HELP_ELEVATION")}
                      unit={t("FO_UNIT_M")}
                      minValue={instLocCircuitsData?.altitude?.minimum}
                      maxValue={instLocCircuitsData?.altitude?.maximum}
                      onChange={(e) => onChangeAltTbase(e, "altitude")}
                    />
                    <Input
                      dataLength="400"
                      control={control}
                      type={InputType.Number}
                      disabled={!location || isLocationInfoFetching}
                      name="t_base"
                      label="FO_SIMULATION_BASE_TEMPERATURE"
                      helpText={t("FO_HELP_BASIS_TEMP")}
                      unit={t("FO_UNIT_C")}
                      minValue={instLocCircuitsData?.t_base?.minimum}
                      maxValue={instLocCircuitsData?.t_base?.maximum}
                      onChange={(e) => onChangeAltTbase(e, "t_base")}
                    />
                  </group>
                  <group data-gap="20">
                    {hasEcs && (
                      <Input
                        dataLength="400"
                        control={control}
                        type={InputType.Number}
                        disabled={!location || isLocationInfoFetching}
                        name="t_ete"
                        label="FO_SIMULATION_SUMMER_TEMPERATURE"
                        helpText={t("FO_HELP_SUMMER_TEMP")}
                        unit={t("FO_UNIT_C")}
                        minValue={instLocCircuitsData?.t_ete?.minimum}
                        maxValue={instLocCircuitsData?.t_ete?.maximum}
                      />
                    )}
                    {(ville?.bdm_montagne_defaut === 1 ||
                      ville?.bdm_montagne_defaut === 2) &&
                      network?.displayInpSeaside && (
                        <OptionBar
                          label={t("FO_SIMULATION_SEASIDE")}
                          control={control}
                          name="bdm_montagne_defaut"
                          options={[
                            {
                              value: "yes",
                              text: t("FO_ETU_YES"),
                            },
                            {
                              value: "no",
                              text: t("FO_ETU_NO"),
                            },
                          ]}
                        />
                      )}
                    {(ville?.bdm_montagne_defaut === 3 ||
                      ville?.bdm_montagne_defaut === 4) && (
                        <OptionBar
                          label={t("FO_SIMULATION_MOUNTAIN")}
                          control={control}
                          name="bdm_montagne_defaut"
                          options={[
                            {
                              value: "yes",
                              text: t("FO_ETU_YES"),
                            },
                            {
                              value: "no",
                              text: t("FO_ETU_NO"),
                            },
                          ]}
                        />
                      )}
                  </group>
                  {hasChauffage && <group data-gap="20" data-align="center" data-length="820">
                    <text>{t("FO_SIMULATION_HEATING_PERIOD")}</text>
                    <Input
                      type={InputType.DatePicker}
                      control={control}
                      label={t("FO_SIMULATION_START_PERIOD")}
                      name="debut_nc"
                      format="DD/MM"
                      outFormat="DD/MM"
                      popupClassName="hide-year-picker"
                      helpText={t("FO_HELP_START_PERIOD")}
                      allowClear={false}
                      required={true}
                      onChange={(e: any) => {
                        setValue("fin_nc", null, { shouldDirty: false });
                        setValue("debut_nc", e, { shouldDirty: true, shouldValidate: true });
                      }}
                    />
                    <Input
                      type={InputType.DatePicker}
                      control={control}
                      label={t("FO_SIMULATION_END_PERIOD")}
                      name="fin_nc"
                      format="DD/MM"
                      outFormat="DD/MM"
                      popupClassName="hide-year-picker"
                      helpText={t("FO_HELP_END_PERIOD")}
                      disabled={!startPeriod}
                      minValue={endPeriodMinDate ? endPeriodMinDate.valueOf() : undefined}
                      defaultPickerValue={endPeriodMinDate || undefined}
                      allowClear={false}
                      required={true}
                    />
                  </group>
                  }
                  {installationType === "2" && <group data-gap="20" data-direction="column" >
                    <group data-width="auto">
                      <Input
                        name="TcoupureQuestion"
                        control={control}
                        type="Checkbox"
                        label={t("FRONT_SIMULATION_TCOUPURE_QUESTION")}
                        defaultChecked={false}
                        helpText={t("FRONT_HELP_TCOUPURE")}
                        info={t("FO_SIMULATION_TCOUPURE_QUESTION_INFO")}
                      />
                    </group>
                    {watch('TcoupureQuestion') && <group data-width="auto">
                      <Input
                        dataLength="400"
                        control={control}
                        type={InputType.Number}
                        disabled={!location || isLocationInfoFetching}
                        name="Tcoupure"
                        label="FRONT_SIMULATION_TCOUPURE"
                        // helpText={t("FO_HELP_BASIS_TEMP")}
                        unit={t("FO_UNIT_C")}
                        minValue={instLocCircuitsData?.Tcoupure?.minimum}
                        maxValue={instLocCircuitsData?.Tcoupure?.maximum}
                      // onChange={(e) => onChangeTcoupure(e)}
                      />
                    </group>}
                  </group>}
                </group>
              </ProgressBarItem>
              <ProgressBarItem
                isOpen={!isHybridBlocked && !!locationInfo}
                title={t("FO_SIMULATION_INSTALLATION")}
              >
                <group data-direction="column" data-gap="10">
                  <group data-gap="20">
                    <Input
                      dataLength="400"
                      label="FO_SIMULATION_DISTANCE_PAC_TANK"
                      type={InputType.Number}
                      name="distance_pac_ballon"
                      control={control}
                      helpText={t("FO_HELP_DIST_HP_TANK")}
                      unit={t("FO_UNIT_M")}
                      minValue={
                        instLocCircuitsData?.distance_pac_ballon?.minimum
                      }
                      maxValue={
                        instLocCircuitsData?.distance_pac_ballon?.maximum
                      }
                    />
                    <Input
                      dataLength="400"
                      label={t("FO_SIMULATION_DISTANCE_PAC_BUILDING")}
                      type={InputType.Number}
                      name="distance_pac_batiment"
                      control={control}
                      helpText={t("FO_HELP_DIST_HP_BUILD")}
                      unit={t("FO_UNIT_M")}
                      minValue={
                        instLocCircuitsData?.distance_pac_batiment?.minimum
                      }
                      maxValue={
                        instLocCircuitsData?.distance_pac_batiment?.maximum
                      }
                    />
                  </group>
                  <group data-gap="20">
                    <OptionBar
                      label={t("FO_SIMULATION_ANTI_CORROSION")}
                      control={control}
                      helpText={t("FO_HELP_ANTI_CORR")}
                      name="antiCorrosion"
                      options={[
                        {
                          value: "yes",
                          text: t("FO_ETU_YES"),
                        },
                        {
                          value: "no",
                          text: t("FO_ETU_NO"),
                        },
                      ]}
                    />
                    {installationType !== null && installationType === "2" && hasEcs && (
                      <OptionBar
                        label={t("FO_SIMULATION_BOILER_FOR_ECS")}
                        control={control}
                        name="boilerForEcs"
                        options={[
                          {
                            value: "yes",
                            text: t("FO_ETU_YES"),
                          },
                          {
                            value: "no",
                            text: t("FO_ETU_NO"),
                          },
                        ]}
                      />
                    )}
                  </group>
                  <group></group>
                  {!hasEcs && (
                    <InstallationRestrictions
                      dataWidth="auto"
                      allData={instLocCircuitsData}
                      calculate2={calculate2}
                      calculationTriggered={calculationTriggered}
                    ></InstallationRestrictions>
                  )}
                </group>
              </ProgressBarItem>
              {hasChauffage && (
                <>
                  <ProgressBarItem
                    title={t("FO_SIMULATION_HEATING_CIRCUITS")}
                    isOpen={!isHybridBlocked && !!locationInfo}
                    stepNumber={5}
                  >
                    <group data-gap="20">
                      <CircuitList
                        control={control}
                        name="circuits"
                        maxCount={5}
                        allData={instLocCircuitsData}
                        service={service}
                      />
                    </group>
                  </ProgressBarItem>
                </>
              )}
              {hasEcs && (
                <>
                  <ProgressBarItem
                    isOpen={!isHybridBlocked && !!locationInfo}
                    title={t("FO_SIMULATION_BUILD_TYPE")}
                  >
                    <Radio
                      type="images"
                      control={control}
                      name="application"
                      options={
                        network?.services
                          .find((s) => s.code === service)
                          ?.applications?.filter(
                            (a) => a.subApplications !== null
                          )
                          .map((a) => ({
                            value: a.code,
                            text: t(a.label),
                            image: (applicationImageMap as any)[a.code],
                            needDarkImage: true,
                          })) ?? []
                      }
                    />
                  </ProgressBarItem>

                  <group data-align="start" data-gap="20">
                    <group data-length="autofit">
                      <ProgressBarItem
                        isOpen={!isHybridBlocked && calculationFormIsOpen}
                        title={t("FO_SIMULATION_BUILD_DESCRIPTION")}
                        stepNumber={6}
                      >
                        <form
                          ref={calculationFormRef}
                          data-type="group"
                          onSubmit={(e) => {
                            e.preventDefault();
                          }}
                        >
                          <group data-gap="20">
                            <group data-gap="20">
                              <group
                                data-gap="10"
                                data-length="autofit"
                                //data-fit="3"
                                data-direction="column"
                              >
                                <group
                                //data-position="center"
                                >
                                  <Input
                                    type={InputType.Select}
                                    name="subApplication"
                                    control={control}
                                    label="FO_SIMULATION_INFRASTRUCTURE_TYPE"
                                    translateLabel
                                    disabled={
                                      subApplicationOptions.length === 1
                                    }
                                    options={subApplicationOptions}
                                  />
                                </group>
                                {fields}
                              </group>
                            </group>

                            {subApplication &&
                              network?.displayInpExpertMode &&
                              subApplication !=
                              SubAppCode.PROCESSUSINDUSTRIEL && (
                                <group data-gap="20">
                                  <group
                                    data-gap="10"
                                    data-length="autofit"
                                    //data-max-length="600"
                                    data-direction="column"
                                  >
                                    <group
                                      data-direction="column"
                                      data-space="20"
                                      data-gap="20"
                                      data-background="accent-xtralight"
                                      data-radius="5"
                                    >
                                      <group
                                      //data-space="10"
                                      >
                                        <Input
                                          type={InputType.Checkbox}
                                          simple
                                          label={t("FO_BUTTON_EXPERT")}
                                          control={control}
                                          name="expertMode"
                                        />

                                        {watch("expertMode") &&
                                          watch("application") && (
                                            <group>
                                              <ExpertModeFields
                                                allData={allData}
                                                calculate={calculate}
                                                autoRecalc={autoRecalc}
                                                setCallCalculate2={
                                                  setCallCalculate2
                                                }
                                              />
                                              <group data-space="20">
                                                <Button
                                                  data-position="center"
                                                  primary
                                                  text={t(
                                                    "restaurer_les_valeurs_par_defaut"
                                                  )}
                                                  onClick={
                                                    onClickRestoreExpertMode
                                                  }
                                                  onMouseDown={(e) =>
                                                    e.preventDefault()
                                                  }
                                                />
                                              </group>
                                            </group>
                                          )}
                                      </group>
                                    </group>
                                  </group>
                                </group>
                              )}

                            {besoinCalculatedData &&
                              !("detail" in besoinCalculatedData) &&
                              !!subApplication ? (
                              <group
                                //data-length="480"
                                data-height="fit"
                              ></group>
                            ) : (
                              calculationFormIsOpen &&
                              !!subApplication && (
                                <group
                                  //data-length="480"
                                  data-direction="column"
                                >
                                  <group>
                                    <Button
                                      data-position="center"
                                      large
                                      primary
                                      text={t("estimation_des_besoins_ecs")}
                                      onClick={() => {
                                        calculate(); // run the calculation
                                        setAutoRecalc(true); // enable auto-recalculation after first click
                                      }}
                                    />
                                  </group>
                                </group>
                              )
                            )}
                          </group>
                        </form>
                      </ProgressBarItem>
                      <ProgressBarItem
                        stepNumber={7}
                        title={t("FO_SIMULATION_ECS_PRODUCTION")}
                        isOpen={
                          !isHybridBlocked &&
                          calculationFormIsOpen &&
                          besoinCalculatedData !== null
                        }
                      >
                        <EcsProductionItem
                          network={network}
                          service={service}
                          application={application}
                          subApplication={subApplication}
                          systeme={systeme}
                          setCalculatedSolutions={setCalculatedSolutions}
                          setLoadingCalculatedData={setLoadingCalculatedData}
                          sendRequest={sendRequest}
                          calculate2={calculate2}
                          calculationTriggered={calculationTriggered}
                          setValidationSchema={setValidationSchema}
                          control={control}
                          handleBlur={handleBlur}
                        />
                        {besoinCalculatedData &&
                          !("detail" in besoinCalculatedData) &&
                          !!subApplication && (
                            <group
                              className="group-fixed-top"
                              data-direction="column"
                              data-gap="20"
                              data-justify="space-between"
                            >
                              <group data-justify="center" data-align="center">
                                {systeme && !calculationTriggered && (
                                  <Button
                                    primary
                                    large
                                    text={t("FO_CALCUL_BUTTON")}
                                    onClick={handleCalculate}
                                  />
                                )}
                              </group>
                            </group>
                          )}
                      </ProgressBarItem>
                      <ProgressBarItem
                        title={t("FO_SIMULATION_ECS_SELECTED_PRODUCT")}
                        isOpen={!isHybridBlocked && isEcsLastStepOpen}
                        stepNumber={8}
                      >
                        <group data-gap="20">
                          <group>
                            <InstallationRestrictions
                              dataWidth="autofit"
                              allData={instLocCircuitsData}
                              calculate2={calculate2}
                              calculationTriggered={calculationTriggered}
                            ></InstallationRestrictions>
                          </group>

                          <group
                            data-direction="column"
                            data-space="20"
                            data-gap="20"
                            data-background="accent-xtralight"
                            data-radius="5"
                          >
                            <group data-gap="20">
                              <group data-direction="column" data-gap="10">
                                <Input
                                  type={InputType.Checkbox}
                                  simple
                                  label={t("FO_SIMULATION_OPTION")}
                                  control={control}
                                  name="simulationOption"
                                />
                                {watch("projectType") === "2" && (
                                  <group
                                    data-gap="10"
                                    data-align="start"
                                    data-wrap="no"
                                  >
                                    <picture
                                      data-height="24"
                                      data-space-top="5"
                                      style={{ width: "auto" }}
                                    >
                                      <img
                                        src={logoUnderOptionSup}
                                        alt="logo"
                                      />
                                    </picture>
                                    <text
                                      data-color="main"
                                      data-text-size="large"
                                      data-wrap="wrap"
                                    >
                                      {t("FO_SIMULATION_RE2020_COMMENT")}
                                    </text>
                                  </group>
                                )}
                              </group>
                              <SimulationOptions
                                engineData={simulationOptionsData}
                                calculate2={calculate2}
                                calculationTriggered={calculationTriggered}
                                isVisible={
                                  getValues("simulationOption") as boolean
                                }
                              />
                            </group>
                          </group>
                          {calculatedSolutions?.solutions &&
                            calculatedSolutions?.solutions.length > 0 &&
                            !("detail" in calculatedSolutions) ? (
                            <group data-gap="20">
                              <group data-gap="20">
                                <group data-length="autofit">
                                  <SolutionRadio
                                    name="etudeFormSolutionId"
                                    control={control}
                                    systeme={systeme}
                                    options={
                                      calculatedSolutions?.solutions.map(
                                        (s, i) => ({
                                          //volume: s.volume,
                                          puissance: s.puissance,
                                          references_commerciales:
                                            s.references_commerciales,
                                          value: i.toString(),
                                          optimale: s.optimale,
                                        })
                                      ) ?? []
                                    }
                                  />
                                  <group
                                    // data-length="5"
                                    data-align="center"
                                    data-justify=""
                                    data-space="5"
                                    data-gap="5"
                                  >
                                    <icon data-fill data-color="main">
                                      star
                                    </icon>
                                    <text>{t("FO_SIMULATION_PRECO")}</text>
                                  </group>
                                </group>
                              </group>
                            </group>
                          ) : (
                            <group data-text-size="15">
                              <text data-weight="400" data-wrap="wrap">
                                <div dangerouslySetInnerHTML={{ __html: t("FO_NOSOLUTION_ECS_HP") }} />
                              </text>
                            </group>
                          )}
                        </group>
                      </ProgressBarItem>
                    </group>

                    {besoinCalculatedData && isBesoinCalculatedDataOpen && !("detail" in besoinCalculatedData) && !!subApplication ? (
                      <group
                        data-length="480"
                        className="group-fixed-top"
                        data-direction="column"
                        data-gap="20"
                        data-justify="space-between"
                      >
                        <group>
                          <group
                            data-space="20"
                            data-length="480"
                            data-background="accent-semilight"
                            data-color="grey-color-dark"
                            data-radius="3"
                            data-gap="20"
                            data-text-size="medium"
                            data-direction="column"
                          >
                            <group data-text-size="22">
                              <text data-weight="500">
                                {t("estimation_des_besoins_ecs")}
                              </text>
                            </group>
                            {Object.entries(calculatedDataOrder).map(
                              ([key, value]) => {
                                const data =
                                  besoinCalculatedData[
                                  key as keyof typeof besoinCalculatedData
                                  ];
                                const typedKey = key as keyof EtudeFormFields;

                                if (
                                  key === "v10" &&
                                  application === AppCode.RESTAURANT
                                ) {
                                  return;
                                }

                                return (
                                  <group key={key}>
                                    <input
                                      type="hidden"
                                      {...register(typedKey)}
                                      value={data}
                                    />
                                    <group data-length="autofit">
                                      <text data-wrap="wrap">{t(value)}:</text>
                                    </group>
                                    <group data-length="80" data-justify="end">
                                      <text data-wrap="wrap" data-weight="700">
                                        {key === "tph"
                                          ? formatMinutes(data)
                                          : Math.round(+data)}
                                      </text>
                                    </group>
                                    <group
                                      data-length="120"
                                      data-justify="start"
                                      data-space-horizontal="5"
                                    >
                                      {key === "tph"
                                        ? t("FO_ESTI_h")
                                        : t("liters_a_60_c")}
                                    </group>
                                  </group>
                                );
                              }
                            )}
                          </group>
                          {expertModeChanged && (
                            <group data-text-size="17">
                              <text data-weight="500">
                                {t("the_expert_input_data_has_been_modified")}
                              </text>
                              <input
                                type="hidden"
                                {...register("expertModeChanged")}
                                value={expertModeChanged}
                              />
                            </group>
                          )}
                        </group>
                      </group>
                    ) : calculationFormIsOpen && !!subApplication ? (
                      <group
                        data-length="480"
                        data-height="fit"
                        data-direction="column"
                      ></group>
                    ) : (
                      <group data-length="480" data-height="fit"></group>
                    )}
                  </group>
                </>
              )}

              <ProgressBarItem
                title={
                  <>
                    {t("FO_HP_ORDER_DATE")}
                    <i data-space-horizontal="5" data-color="red">*</i>
                  </>}
                isOpen={
                  !isHybridBlocked &&
                  (service === EtudeTypes.ECS ||
                    service === EtudeTypes.chauffageECS
                    ? isEcsLastStepOpen
                    : !!locationInfo)
                }
                stepNumber={service === EtudeTypes.ECS ? 9 : 0}
              >
                <group data-gap="20">
                  <RadioGroupDelay defaultValue={inputDelay} name="inputDelay" control={control} />
                </group>
              </ProgressBarItem>

              <ProgressBarItem
                title={t("FO_SIMULATION_NAME")}
                isOpen={
                  !isHybridBlocked &&
                  (service === EtudeTypes.ECS ||
                    service === EtudeTypes.chauffageECS
                    ? isEcsLastStepOpen
                    : !!locationInfo)
                }
                lastItem
                stepNumber={service === EtudeTypes.ECS ? 10 : 0}
              >
                <group data-gap="20">
                  <group data-length="autofit">
                    <Input
                      type={InputType.TextArea}
                      label="FO_SIMULATION_NAME"
                      name="siteName"
                      control={control}
                      data-resize="none"
                      required={true}
                    />
                  </group>
                </group>
              </ProgressBarItem>
            </ProgressBar>
          </form>
        </FormProvider>
        <group
          data-border="overprint"
          data-background="white"
          data-align="center"
          data-gap="10"
          data-space="10"
          data-justify="end"
          data-sticky="bottom"
        >
          {isAuth && (
            <Button
              onClick={async () => {
                await handleUpdateJson();
                !isLoadingEtudeJsonSave &&
                  openPopup(
                    <SavePopup
                      closePopup={closeLastPopup}
                      application={application || ""}
                      subApplication={subApplication}
                      systeme={systeme || ""}
                      formData={{
                        ...formData,
                        step: EtudeStep.InputForm,
                        stepName: t("FO_STUDIES_IN_PROGRESS"),
                        service: service,
                        ville: ville,
                      }}
                    ></SavePopup>
                  );
              }}
              primary
              large
              text="Sauvegarder"
              icon="save"
            />
          )}
          <Button
            primary
            large
            text="FO_BUTTON_NEXT_STEP"
            icon="arrow_forward"
            disabled={
              isHybridBlocked ||
              (service === EtudeTypes.ECS || service === EtudeTypes.chauffageECS
                ? !isEcsSaveOpen
                : !locationInfo)
            }
            type="submit"
            onClick={() => {
              handleSubmit(onSubmit, undefined, onInvalid)();
            }}
          />
        </group>
      </group>
    </group>
  );
};
